import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { HeartPulse, Brain, Heart, Sparkles, Users, Sunrise } from "lucide-react";

const PILLARS = [
  { id: "physical", icon: HeartPulse, label: "Physical", pos: { top: "10%", left: "50%" }, desc: "Medically supervised stabilization, nutritious meals and rest to restore your body." },
  { id: "mental", icon: Brain, label: "Mental", pos: { top: "38%", left: "88%" }, desc: "CBT, DBT and SMART Recovery to gently retrain the thoughts that fuel addiction." },
  { id: "emotional", icon: Heart, label: "Emotional", pos: { top: "82%", left: "72%" }, desc: "Individual and group therapy to safely process trauma, shame and difficult feelings." },
  { id: "spiritual", icon: Sparkles, label: "Spiritual", pos: { top: "82%", left: "28%" }, desc: "Mindfulness, meditation and the 12 steps to reconnect with meaning and purpose." },
  { id: "community", icon: Users, label: "Community", pos: { top: "38%", left: "12%" }, desc: "Family care, aftercare and a recovery community that walks with you for a lifetime." },
];

const InteractiveJourney = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const ActiveIcon = PILLARS[active].icon;

  return (
    <section data-testid="interactive-journey" className="relative bg-forestdark overflow-hidden py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4 font-semibold">Our Whole‑Person Approach</p>
          <h2 className="font-serif text-4xl md:text-6xl text-sand tracking-tight leading-[1.02]">
            We heal every part of <span className="italic text-terracotta">you</span>
          </h2>
          <p className="mt-4 text-sand/60">Explore the five dimensions of care that make lasting recovery possible.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Interactive orbit */}
          <div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative mx-auto w-full max-w-[460px] aspect-square"
            style={{ perspective: "1000px" }}
          >
            <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative w-full h-full">
              {/* dashed ring */}
              <div className="absolute inset-[14%] rounded-full border border-dashed border-sand/20" />

              {/* center sun */}
              <div className="absolute top-1/2 left-1/2 z-0 -translate-x-1/2 -translate-y-1/2">
                <motion.div
                  className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-terracotta to-[#e8a07f] shadow-[0_0_70px_rgb(206,106,75,0.45)] grid place-items-center"
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Sunrise className="w-11 h-11 text-sand" strokeWidth={1.4} />
                </motion.div>
              </div>

              {/* pillars */}
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                const on = active === i;
                return (
                  <button
                    key={p.id}
                    data-testid={`pillar-${p.id}`}
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => setActive(i)}
                    style={{ top: p.pos.top, left: p.pos.left }}
                    className="absolute z-10 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 group"
                  >
                    <span className={`grid place-items-center w-16 h-16 rounded-full border transition-all duration-300 ${on ? "bg-sand text-forest border-sand scale-110" : "bg-forest/60 text-sand border-sand/25 group-hover:border-sage"}`}>
                      <Icon className="w-6 h-6" />
                    </span>
                    <span className={`text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${on ? "text-white" : "text-white/80"}`}>{p.label}</span>
                  </button>
                );
              })}
            </motion.div>
          </div>

          {/* Detail panel */}
          <div className="lg:pl-8">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl bg-forest/50 border border-sand/10 p-8 md:p-10"
            >
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-terracotta/20 text-terracotta mb-6">
                <ActiveIcon className="w-6 h-6" />
              </span>
              <h3 className="font-serif text-3xl md:text-4xl text-sand">{PILLARS[active].label} Care</h3>
              <p className="mt-4 text-lg text-sand/75 leading-relaxed">{PILLARS[active].desc}</p>
              <div className="mt-8 flex flex-wrap gap-2">
                {PILLARS.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => setActive(i)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === i ? "bg-sand text-forest" : "bg-forest/60 text-white hover:text-white"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveJourney;
